/**
 * WalletLockScreen Component
 *
 * Full-screen lock screen for returning users to unlock their wallet by entering their password.
 * Displayed when sessionStorage password is cleared (e.g., after app restart).
 * This is a standalone screen (not an overlay) - the main app is not rendered until unlocked.
 */

import React, { useState, useCallback, useEffect, useRef } from "react";
import { useSatchelWallet, Button, ASSET_PATHS } from "@satchel/core";
import { siteMetadata } from "../../siteMetadata";

export interface WalletLockScreenProps {
  /** Callback when wallet is successfully unlocked */
  onUnlock?: () => void;
  /** Callback when user wants to recover with phrase (import existing wallet) */
  onRecoverWithPhrase?: () => void;
  /** Callback when user wants to create a new wallet */
  onCreateNew?: () => void;
}

export const WalletLockScreen: React.FC<WalletLockScreenProps> = ({
  onUnlock,
  onRecoverWithPhrase,
  onCreateNew,
}) => {
  const wallet = useSatchelWallet();
  const { restoreWithPassword } = wallet;
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Seed with wallet context error (e.g., "Incorrect password" from a previous failed attempt)
  const [error, setError] = useState<string | null>(wallet.error);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus password input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleUnlock = useCallback(async () => {
    if (!password.trim()) {
      setError("Please enter your password");
      return;
    }

    setIsUnlocking(true);
    setError(null);

    try {
      // Restore wallet access with the provided password
      restoreWithPassword(password);

      // If successful, call the onUnlock callback
      if (onUnlock) {
        onUnlock();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to unlock");
    } finally {
      setIsUnlocking(false);
    }
  }, [password, restoreWithPassword, onUnlock]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && password.trim()) {
        handleUnlock();
      }
    },
    [password, handleUnlock],
  );

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center p-4 bg-sa-bg"
      data-testid="wallet-lock-screen"
    >
      {/* Logo and branding */}
      <div className="mb-8 text-center">
        <img
          src={ASSET_PATHS.SATCHEL_LOGO}
          alt={siteMetadata.title}
          className="w-40 h-40 mx-auto mb-4"
        />
        <h1 className="text-2xl font-bold text-sa-text">{siteMetadata.title}</h1>
        <p className="mt-1 text-sm text-sa-text-muted">{siteMetadata.expansion}</p>
      </div>

      {/* Unlock card */}
      <div className="w-full max-w-md rounded-lg border border-sa-border bg-sa-surface">
        {/* Header */}
        <div className="border-b border-sa-border p-6">
          <h2 className="text-xl font-bold text-sa-text">Unlock SATCHEL</h2>
          <p className="mt-1 text-sm text-sa-text-muted">Enter your password to continue</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Password input */}
          <div>
            <label
              htmlFor="unlock-password"
              className="mb-1 block text-sm font-medium text-sa-text"
            >
              Password
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                type={showPassword ? "text" : "password"}
                id="unlock-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your password"
                disabled={isUnlocking}
                autoComplete="current-password"
                data-testid="unlock-password-input"
                className={`
                  w-full px-4 py-3 pr-16 rounded-lg border
                  bg-sa-bg
                  text-sa-text
                  placeholder-sa-text-dim
                  focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary
                  disabled:opacity-50
                  ${error ? "border-sa-danger" : "border-sa-border"}
                `}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isUnlocking}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-sa-text-muted hover:text-sa-text disabled:opacity-50"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {error && (
              <p className="mt-1 text-sm text-sa-danger" data-testid="unlock-error">
                {error}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-sa-border p-6">
          <Button
            variant="primary"
            onClick={handleUnlock}
            disabled={isUnlocking || !password.trim()}
            data-testid="unlock-submit"
            className="w-full"
          >
            {isUnlocking ? "Unlocking..." : "Unlock"}
          </Button>
        </div>
      </div>

      {/* Alternative actions */}
      <div className="mt-6 text-center space-y-2">
        {onRecoverWithPhrase && (
          <button
            type="button"
            onClick={onRecoverWithPhrase}
            disabled={isUnlocking}
            className="text-sm text-primary hover:text-primary-hover hover:underline disabled:opacity-50"
            data-testid="recover-with-phrase"
          >
            Forgot password? Recover with phrase
          </button>
        )}
        {onCreateNew && (
          <div>
            <button
              type="button"
              onClick={onCreateNew}
              disabled={isUnlocking}
              className="text-sm text-sa-text-muted hover:text-sa-text hover:underline disabled:opacity-50"
              data-testid="create-new-wallet"
            >
              Set up a new SATCHEL
            </button>
          </div>
        )}
      </div>

      {/* Version info */}
      <p className="mt-4 text-xs text-sa-text-dim">{siteMetadata.versionLabel}</p>
    </div>
  );
};

// Keep backward-compatible export name for existing imports
export const PasswordUnlockModal = WalletLockScreen;
export default WalletLockScreen;
